import * as React from "react";
import { cn } from "@/lib/utils";
import { X, SideOpen, Edit, Trash, CircleCheck } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { formatDateKST } from "@/common/utils/date";

export interface CloseButtonProps {
  /** 닫기 버튼 클릭 핸들러 */
  onClose: () => void;
  /** 아이콘 크기 */
  size?: number;
  /** 추가 className */
  className?: string;
}

/**
 * 공통 닫기 버튼 컴포넌트
 * Modal, Drawer 등에서 사용
 */
export function CloseButton({
  onClose,
  size = 24,
  className,
}: CloseButtonProps) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={cn(
        "shrink-0 size-6 flex items-center justify-center",
        className,
      )}
    >
      <X size={size} color="labels-assistive" />
    </button>
  );
}

CloseButton.displayName = "CloseButton";

export interface ModalHeaderProps {
  /** 모달 제목 */
  title: string;
  /** 닫기 버튼 클릭 핸들러 */
  onClose: () => void;
  /** 제목 왼쪽에 표시할 아이콘 (선택) */
  leftIcon?: React.ReactNode;
  /** 닫기 버튼 왼쪽에 표시할 액션 버튼들 (선택) */
  actions?: React.ReactNode;
  /** 추가 className */
  className?: string;
  /** 하단 보더 표시 여부 */
  showBorder?: boolean;
}

/**
 * 모달 헤더 컴포넌트
 * 타이틀 + 액션 버튼 + 닫기 버튼 구조
 *
 * @example
 * ```tsx
 * <ModalHeader
 *   title="충전소 등록"
 *   onClose={handleClose}
 * />
 *
 * <ModalHeader
 *   title="충전소 수정"
 *   onClose={handleClose}
 *   actions={
 *     <>
 *       <Button onClick={handleCancel}>취소</Button>
 *       <Button onClick={handleSave}>저장</Button>
 *     </>
 *   }
 * />
 * ```
 */
export function ModalHeader({
  title,
  onClose,
  leftIcon,
  actions,
  className,
  showBorder = true,
}: ModalHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-10 pt-10 pb-5 shrink-0",
        showBorder && "inset-border-solid-normal-b",
        className,
      )}
    >
      {leftIcon && <span className="shrink-0">{leftIcon}</span>}
      <span className="flex-1 typo-heading-1-bold text-labels-primary">
        {title}
      </span>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
      <CloseButton onClose={onClose} />
    </div>
  );
}

ModalHeader.displayName = "ModalHeader";

export interface DrawerHeaderProps {
  /** 닫기 버튼 클릭 핸들러 */
  onClose: () => void;
  /** 헤더 왼쪽 영역 콘텐츠 */
  leftContent?: React.ReactNode;
  /** 헤더 오른쪽 액션 버튼들 */
  actions?: React.ReactNode;
  /** 추가 className */
  className?: string;
  /** 하단 보더 표시 여부 */
  showBorder?: boolean;
  /** 자식 요소 (하단 영역에 추가 콘텐츠) */
  children?: React.ReactNode;
  /** 수직 정렬 (default: center) */
  align?: "center" | "start";
}

/**
 * 드로어 헤더 컴포넌트
 * 유연한 구조로 다양한 레이아웃 지원
 *
 * @example
 * ```tsx
 * // 단순 헤더
 * <DrawerHeader
 *   onClose={handleClose}
 *   leftContent={<span className="typo-heading-1-bold">제목</span>}
 *   actions={<Button>수정</Button>}
 * />
 *
 * // 복잡한 헤더 (하단 콘텐츠 포함)
 * <DrawerHeader onClose={handleClose} leftContent={...} actions={...}>
 *   <div className="px-10">추가 콘텐츠</div>
 * </DrawerHeader>
 * ```
 */
export function DrawerHeader({
  onClose,
  leftContent,
  actions,
  className,
  showBorder = true,
  children,
  align = "center",
}: DrawerHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col shrink-0",
        showBorder && "inset-border-solid-normal-b",
        className,
      )}
    >
      <div
        className={cn(
          "flex justify-between px-10 pt-10 pb-3",
          align === "center" ? "items-center" : "items-start",
        )}
      >
        <div
          className={cn(
            "flex gap-3",
            align === "center" ? "items-center" : "items-start",
          )}
        >
          {leftContent}
        </div>
        <div className="flex items-center gap-3">
          {actions}
          <CloseButton onClose={onClose} />
        </div>
      </div>
      {children}
    </div>
  );
}

DrawerHeader.displayName = "DrawerHeader";

// ============================================
// EditModeHeader
// ============================================
export interface EditModeHeaderProps {
  /** 헤더 제목 */
  title: string;
  /** 닫기 버튼 클릭 핸들러 */
  onClose: () => void;
  /** 취소 버튼 클릭 핸들러 */
  onCancel: () => void;
  /** 폼 제출 중 상태 */
  isSubmitting?: boolean;
  /** 취소 버튼 텍스트 */
  cancelLabel?: string;
  /** 저장 버튼 텍스트 */
  submitLabel?: string;
  /** 저장 중 버튼 텍스트 */
  submittingLabel?: string;
  /** 저장 버튼 아이콘 (기본: 원형 체크) */
  submitIcon?: React.ReactNode;
}

/**
 * 수정 모드 헤더 컴포넌트
 * Drawer에서 수정 모드일 때 사용
 *
 * @example
 * ```tsx
 * <EditModeHeader
 *   title="충전소 수정"
 *   onClose={handleClose}
 *   onCancel={handleCancel}
 *   isSubmitting={isSubmitting}
 * />
 * ```
 */
export function EditModeHeader({
  title,
  onClose,
  onCancel,
  isSubmitting = false,
  cancelLabel = "수정 취소",
  submitLabel = "저장",
  submittingLabel = "저장 중...",
  submitIcon = <CircleCheck size={16} />,
}: EditModeHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between",
        "px-10 pt-10 pb-3",
        "shrink-0",
      )}
    >
      <div className="flex items-center gap-3">
        <SideOpen color="labels-assistive" onClick={onClose} />
        <span className="typo-heading-1-bold text-labels-primary">{title}</span>
      </div>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          theme="assistive"
          variant="outline"
          size="sm"
          onClick={onCancel}
        >
          <Trash size={16} />
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          theme="gradient"
          variant="default"
          size="sm"
          disabled={isSubmitting}
        >
          {submitIcon}
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
      </div>
    </div>
  );
}

EditModeHeader.displayName = "EditModeHeader";

// ============================================
// ViewModeHeader
// ============================================
export interface ViewModeHeaderProps {
  /** 메인 제목 (예: 충전소명) */
  title: string;
  /** 부제목 (예: ID) */
  subtitle?: string | number;
  /** 제목 옆에 표시할 추가 요소 (예: 상태 Badge) */
  titleExtra?: React.ReactNode;
  /** 닫기 버튼 클릭 핸들러 */
  onClose: () => void;
  /** 수정 버튼 클릭 핸들러 */
  onEdit: () => void;
  /** 삭제 버튼 클릭 핸들러 */
  onDelete: () => void;
  /** 등록일시 */
  createdAt?: string | null;
  /** 최근수정일시 */
  updatedAt?: string | null;
  /** 쓰기 권한 (기본 true). false 이면 수정 버튼을 그리지 않는다 */
  canWrite?: boolean;
  /**
   * 권한은 있으나 상황상 수정할 수 없는 사유 (로딩 중, 플랫폼 계정 등).
   * 주면 버튼이 사유 툴팁과 함께 비활성으로 남는다 — 권한 부족과 구분한다
   */
  writeDisabledReason?: string;
  /** 삭제 권한 (기본 canWrite). 수정은 허용하되 삭제만 막는 경우에 지정 */
  canDelete?: boolean;
  /** 삭제만의 비활성 사유 (기본 writeDisabledReason) */
  deleteDisabledReason?: string;
  /**
   * 수정·삭제 버튼을 아예 그리지 않는다. 권한이 모자란 것(비활성 + 사유 툴팁)과 달리,
   * 그 소속에게는 존재하지 않는 기능일 때 쓴다 (예: 고객사의 충전기 상세)
   */
  hideActions?: boolean;
  /** 제목 바로 아래에 붙는 요소 (예: 상세 화면 전환 탭). 본문과 달리 스크롤되지 않는다 */
  belowTitle?: React.ReactNode;
}

/**
 * 조회 모드 헤더 컴포넌트
 * Drawer에서 조회 모드일 때 사용
 *
 * @example
 * ```tsx
 * <ViewModeHeader
 *   title={stationInfo?.name || "충전소명"}
 *   subtitle={stationInfo?.id}
 *   onClose={handleClose}
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 *   createdAt={stationInfo?.created_at}
 *   updatedAt={stationInfo?.updated_at}
 * />
 * ```
 */
export function ViewModeHeader({
  title,
  subtitle,
  titleExtra,
  onClose,
  onEdit,
  onDelete,
  createdAt,
  updatedAt,
  canWrite = true,
  writeDisabledReason,
  canDelete = canWrite,
  deleteDisabledReason = writeDisabledReason,
  hideActions = false,
  belowTitle,
}: ViewModeHeaderProps) {
  return (
    <div className="flex flex-col gap-6 pt-10 shrink-0">
      {/* 상단: 등록일시/최근수정 + 버튼 */}
      <div className="flex items-start justify-between px-10">
        <div className="flex gap-3 items-start">
          <SideOpen color="labels-assistive" onClick={onClose} />
          {/* 등록일시/최근수정 정보 박스 */}
          <div className="flex flex-col gap-1.5 px-3 py-1.5 bg-fills-neutral rounded-xl">
            <div className="flex gap-2 items-center">
              <span className="typo-caption-1-medium text-labels-primary w-12">
                등록일시
              </span>
              <span className="typo-caption-1-medium text-labels-assistive">
                {createdAt ? formatDateKST(createdAt, { ampm: true }) : "-"}
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="typo-caption-1-medium text-labels-primary w-12">
                최근 수정
              </span>
              <span className="typo-caption-1-medium text-labels-assistive">
                {updatedAt ? formatDateKST(updatedAt, { ampm: true }) : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!hideActions && (
            <>
              {canWrite && (
                <span title={writeDisabledReason}>
                  <Button
                    type="button"
                    theme="assistive"
                    variant="outline"
                    size="sm"
                    onClick={onEdit}
                    disabled={!!writeDisabledReason}
                  >
                    <Edit size={16} />
                    수정
                  </Button>
                </span>
              )}
              {canDelete && (
                <span title={deleteDisabledReason}>
                  <Button
                    type="button"
                    theme="assistive"
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    disabled={!!deleteDisabledReason}
                  >
                    <Trash size={16} color="status-urgent" />
                    삭제
                  </Button>
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* 하단: 제목 + 부제목 + titleExtra, 그 아래 belowTitle */}
      <div className="flex flex-col gap-2 px-10">
        <div className="flex gap-2 items-center min-w-0">
          <span className="typo-heading-1-bold text-labels-primary truncate min-w-0">
            {title}
          </span>
          {subtitle && (
            <span className="typo-body-1-medium text-labels-assistive shrink-0">
              {subtitle}
            </span>
          )}
          {titleExtra}
        </div>
        {belowTitle}
      </div>
    </div>
  );
}

ViewModeHeader.displayName = "ViewModeHeader";
